import type { Mesh, Object3D, Vector3 } from '@anov/3d-core'
import { Color, ShaderMaterial, use } from '@anov/3d-core'

import { createRoundLineWallMesh, createRoundedClosedWallMesh } from '../../Utils/round-curve'
import type { FenceOption } from '../type'
import { hexToRGBA, parseRGBA } from '../tool'
import FenceBase from './FenceBase'

class Fence_Feeting_01 extends FenceBase {
  constructor(option: FenceOption, areaInstance: Object3D) {
    super(option, areaInstance)
    this.create()
  }

  create() {
    this._mesh = this.createFenceMesh(
      this._points,
      this._corner,
      this._close,
      this._height,
      this._color,
      this._alpha,
    )
    this._mesh && this.add(this._mesh)
  }

  // 创建围栏网格对象
  createFenceMesh(points: Vector3[], radius = 0, close = true, height: number, color: string, alpha: number) {
    if (!points)
      return
    // 创建围栏使用的材质，调用createFenceMaterial方法生成ShaderMaterial类型的材质
    const material = this.createFenceMaterial(height, color, alpha)

    // 使用createRoundLineWallMesh函数创建围栏的网格物体，传入点数组、半径、高度、是否闭合等参数以及创建好的材质
    const mesh = createRoundedClosedWallMesh(
      points,
      {
        radius,
        height,
        close,
      },
      material,
    )
    // const mesh = createRoundLineWallMesh(
    //   points,
    //   {
    //     radius,
    //     height,
    //     close,
    //   },
    //   material,
    // )
    use.useframe(this.sceneControl, () => {
      // mesh.children.forEach((item, index) => {
      //   const uniforms = ((item as Mesh).material as ShaderMaterial).uniforms
      //   uniforms.iTime.value += 0.01
      // })

      const uniforms = ((mesh as Mesh).material as ShaderMaterial).uniforms
      uniforms.iTime.value += 0.002
    })
    // 创建围栏使用的材质，调用createFenceMaterial方法生成ShaderMaterial类型的材质
    // const material = this.createFenceMaterial(height, color, alpha)
    // // 使用createRoundLineWallMesh函数创建围栏的网格物体，传入点数组、半径、高度、是否闭合等参数以及创建好的材质
    // const mesh = createRoundLineWallMesh(
    //   points,
    //   {
    //     radius,
    //     height,
    //     close,
    //   },
    //   material,
    // )
    // use.useframe(() => {
    //   mesh.children.forEach((item, index) => {
    //     const uniforms = ((item as Mesh).material as ShaderMaterial).uniforms
    //     uniforms.iTime.value += 0.002
    //   })
    // })
    return mesh
  }

  // 创建围栏材质
  createFenceMaterial(height: number, color: string, alpha: number): ShaderMaterial {
    const { r, g, b } = parseRGBA(hexToRGBA(color))
    const colorTemp = { r: Number((Number(r) / 255).toFixed(4)), g: Number((Number(g) / 255).toFixed(4)), b: Number((Number(b) / 255).toFixed(4)) }
    // 创建一个ShaderMaterial实例，用于自定义围栏的渲染效果
    const material = new ShaderMaterial({
      uniforms: {
        // 材质颜色
        color1: {
          value: colorTemp,
        }, // 材质颜色2，用于渐变
        _alpha: { value: alpha },
        _height: { value: height },
        iTime: { value: 0.0 },
      },
      // 顶点着色器
      vertexShader: `
       #include <common>
            #include <logdepthbuf_pars_vertex>
            varying vec3 vNormal; // 用于传递法线方向
            varying vec2 vUv;
            varying vec3 vWorldPosition;
          void main() {
          	#include <begin_vertex>
            #include <project_vertex>
            #include <logdepthbuf_vertex>
            // gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          vUv = uv;
          vNormal = normalize(mat3(modelMatrix) * normal);  // 使用模型矩阵旋转法线
          vec4 modelPosition = modelMatrix * vec4(position, 1.0);
          vWorldPosition = modelPosition.xyz;
          gl_Position = projectionMatrix * viewMatrix * modelPosition;
          }
      `,
      // 片元着色器
      fragmentShader: `
                #include <common>
         #include <logdepthbuf_pars_fragment>
     varying vec3 vNormal; // 用于传递法线方向
     varying vec2 vUv;
     varying vec3 vWorldPosition;
    
    uniform float _alpha;
    uniform float _height;
   uniform vec3 color1;
   uniform float iTime;
   


const int largeBlockCount = 80;
const int smallBlockCount = 20;
const int layerBlockCount = 10; // 添加的两层方块

const float largeBlockSpeed = .10;
const float smallBlockSpeed = .20;
const float layerBlockSpeed = .15; // 层的浮动速度

const float largeBlockSizeMin = 0.002;
const float largeBlockSizeMax = 0.03;
const float smallBlockSizeMin = 0.003;
const float smallBlockSizeMax = 0.008;
const float layerBlockSizeMin = 0.005;
const float layerBlockSizeMax = 0.01; // 两层方块的大小范围

const vec3 largeBlockColor = vec3(0.3, 0.2, 0.3);
const vec3 smallBlockColor = vec3(0.7, 0.6, 0.9);
const vec3 layerBlockColor = vec3(0.9, 0.7, 0.5); // 新增层的颜色

float hash11(float p) {
return fract(sin(p) * 43758.5453);
}

vec4 mainImage( vec2 uv) {
// vec2 uv = fragCoord.xy / iResolution.xy;
vec3 color = vec3(0.0, 0.0, 0.0); // Background color

// Large blocks
for (int i = 0; i < largeBlockCount; ++i) {
    float id = float(i);
    vec2 randomPos = vec2(hash11(id + 0.1), hash11(id + 0.2));
    randomPos = fract(randomPos + vec2(0.,iTime * largeBlockSpeed)); // Float upwards with time

    float blockSize = mix(largeBlockSizeMin, largeBlockSizeMax, hash11(id));

    vec2 dist = abs(uv - randomPos);
    float inside = step(dist.x, blockSize) * step(dist.y, blockSize);
    color = mix(color, largeBlockColor, inside);
}

// Small blocks
for (int i = 0; i < smallBlockCount; ++i) {
    float id = float(i + largeBlockCount); // Offset for unique small block IDs
    vec2 randomPos = vec2(hash11(id + 0.1), hash11(id + 0.2));
    randomPos = fract(randomPos + vec2(0.,iTime * smallBlockSpeed)); // Float upwards with time

    float blockSize = mix(smallBlockSizeMin, smallBlockSizeMax, hash11(id));

    vec2 dist = abs(uv - randomPos);
    float inside = step(dist.x, blockSize) * step(dist.y, blockSize+hash11(id )*0.01);
    color = mix(color, smallBlockColor, inside);
}

// Layer blocks
for (int i = 0; i < layerBlockCount; ++i) {
    float id = float(i + largeBlockCount + smallBlockCount); // Offset for unique layer block IDs
    vec2 randomPos = vec2(hash11(id + 0.1), hash11(id + 0.2));
    randomPos = fract(randomPos + vec2(0.,iTime * layerBlockSpeed)); // Float upwards with time

    // Randomize the size with a slight stretch for length/width variation
    float blockSizeX = mix(layerBlockSizeMin, layerBlockSizeMax, hash11(id));
    float blockSizeY = blockSizeX * (0.8 + 0.4 * hash11(id + 0.3)); // Randomize aspect ratio

    vec2 dist = abs(uv - randomPos);
    float inside = step(dist.x, blockSizeX+hash11(id )*0.02) * step(dist.y, blockSizeY);
    color = mix(color, layerBlockColor, inside);
}
  return  vec4(color, 1.0);

}

    void main() {
    #include <logdepthbuf_fragment>
   // float height = 50.;
    float button = 0.;
    vec3 myWp=(vWorldPosition-button)/_height;       
       gl_FragColor = vec4(color1,pow(1.-clamp(myWp.y,0.,1.),2.))    *step(button,myWp.y);///0.6,0.1,0.01
       
    if (abs(vNormal.x) > 0.99) { 
        gl_FragColor =mix(gl_FragColor,vec4(color1+vec3(0.2,0.7,0.2),1.*myWp.y) ,mainImage(fract(myWp.zy)).x   *pow(gl_FragColor.a,.1) ); /////vec4(0.9,0.8,0.3,1.)
        }
    if (abs(vNormal.z) > 0.99) { 
        gl_FragColor =mix(gl_FragColor,vec4(color1+vec3(0.2,0.7,0.2),1.*myWp.y) ,mainImage(fract(myWp.xy)).x   *pow(gl_FragColor.a,.1) ); 
        }
      gl_FragColor.a *= _alpha;

    }
      `,
      // 设置材质为透明
      transparent: true,
      // 设置材质双面渲染，确保从正反两面都能正确显示
      side: 2,
      // 关闭深度测试
      depthTest: true,
      depthWrite: false,
    })
    return material
  }
}

export default Fence_Feeting_01
